import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, GitMerge, Loader2 } from 'lucide-react';
import { CATEGORY_META } from './mailUtils';
import { AB_API, abAuth } from './ContactEditor';

/** Same person, several emails → pick the one to keep; the others become aliases. */
export const ContactDuplicates = ({ onClose, onMerged }) => {
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState('');
  useEffect(() => { axios.get(`${AB_API}/duplicates`, abAuth()).then(r => setGroups(r.data)).catch(() => setGroups([])); }, []);
  const merge = async (g, keepId) => {
    setBusy(g.name);
    try {
      await axios.post(`${AB_API}/merge`, { keep_id: keepId, remove_ids: g.contacts.filter(c => c.id !== keepId).map(c => c.id) }, abAuth());
      toast.success(`Merged ${g.contacts.length} entries for ${g.name}`); setGroups(gs => gs.filter(x => x !== g)); onMerged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Merge failed'); }
    setBusy('');
  };
  return createPortal(
    <div className="fixed inset-0 z-[9000] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="contact-dupes-overlay">
      <div className="w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col" onClick={e => e.stopPropagation()} data-testid="contact-dupes">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2"><GitMerge className="w-4 h-4 text-sky-700" />Possible duplicates {groups && <span className="text-slate-400 font-normal normal-case">({groups.length})</span>}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="contact-dupes-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-3">
          {!groups && <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Looking for duplicates…</p>}
          {groups && !groups.length && <p className="text-sm text-slate-500" data-testid="contact-dupes-empty">No duplicates found.</p>}
          {groups?.map(g => (
            <div key={g.name} className="rounded-xl border border-slate-200 p-3" data-testid={`dupe-group-${g.name}`}>
              <p className="text-sm font-bold text-slate-900 mb-1.5">{g.name}</p>
              <ul className="space-y-1">
                {g.contacts.map(c => (
                  <li key={c.id} className="flex items-center gap-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold ${CATEGORY_META[c.category]?.cls}`}>{CATEGORY_META[c.category]?.label}</span>
                    <span className="font-mono text-slate-800">{c.email}</span><span className="text-slate-500 truncate">{c.company}</span>
                    <span className="text-slate-400 ml-auto shrink-0">{c.seen_count ? `${c.seen_count}×` : ''}</span>
                    <button onClick={() => merge(g, c.id)} disabled={!!busy} className="px-2 py-0.5 rounded border border-slate-200 bg-white hover:bg-sky-50 text-slate-800 font-semibold shrink-0" data-testid={`dupe-keep-${c.id}`}>Keep this</button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>, document.body);
};
